import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminMediaItem, AdminGroup } from '@/lib/api'
import AssetImageField from '@/components/AssetImageField'

const BADGE_OPTIONS = ['MV', 'LIVE', 'BEHIND']

interface FormData {
  group_slug: string
  image_url: string
  alt: string
  badge: string
  duration: string
  date: string
  title: string
  is_featured: boolean
  youtube_url: string
}

const blank: FormData = {
  group_slug: '', image_url: '', alt: '', badge: 'MV', duration: '', date: '', title: '',
  is_featured: false, youtube_url: '',
}

function toFormData(m: AdminMediaItem): FormData {
  return {
    group_slug: m.group_slug ?? '', image_url: m.image_url, alt: m.alt,
    badge: m.badge, duration: m.duration, date: m.date, title: m.title,
    is_featured: m.is_featured, youtube_url: m.youtube_url ?? '',
  }
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800">
        <h2 className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  )
}

export default function MediaFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { token, isGroupManager, managedGroupSlug } = useAuth()

  const isEdit = Boolean(id)

  const [form, setForm] = useState<FormData>(blank)
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!token) return

    const fetchGroups = api.getAdminGroups(token)

    if (isEdit) {
      const stateMedia = (location.state as { media?: AdminMediaItem } | null)?.media
      if (stateMedia) {
        fetchGroups.then((groupList) => {
          setGroups(groupList)
          setForm(toFormData(stateMedia))
          setLoading(false)
        })
      } else {
        Promise.all([fetchGroups, api.getAdminMedia(token)]).then(([groupList, mediaList]) => {
          setGroups(groupList)
          const found = mediaList.find((m) => m.id === id)
          if (found) setForm(toFormData(found))
          setLoading(false)
        })
      }
    } else {
      fetchGroups.then((groupList) => {
        setGroups(groupList)
        if (isGroupManager && managedGroupSlug) {
          setForm((prev) => ({ ...prev, group_slug: managedGroupSlug }))
        }
        setLoading(false)
      })
    }
  }, [id, isEdit, token, location.state, isGroupManager, managedGroupSlug])

  const set = <K extends keyof FormData>(field: K, value: FormData[K]) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setSaving(true)
    try {
      const payload = { ...form, group_slug: form.group_slug || null, youtube_url: form.youtube_url || null }
      if (isEdit && id) {
        const updated = await api.updateMedia(token, id, payload)
        navigate(`/media/${id}`, { replace: true, state: { media: updated } })
      } else {
        await api.createMedia(token, payload)
        navigate(-1)
      }
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const inp = 'w-full bg-zinc-800 text-white rounded-lg px-3 py-2 text-sm border border-zinc-700 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-600'
  const lbl = 'block text-xs text-zinc-400 mb-1.5'

  if (loading) {
    return <div className="p-6"><p className="text-zinc-500 text-sm">Loading…</p></div>
  }

  return (
    <div className="p-6">
      <form onSubmit={handleSubmit}>
        {/* Sticky top bar */}
        <div className="sticky top-0 z-10 bg-zinc-950 flex items-center justify-between pb-4 mb-6 border-b border-zinc-800">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 transition-colors mb-3"
            >
              <ChevronLeft size={14} />
              Kembali
            </button>
            <h1 className="text-xl font-bold text-white">
              {isEdit ? 'Edit Media' : 'New Media'}
            </h1>
            {isEdit && <p className="text-zinc-500 text-sm mt-1">{form.title}</p>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-5 py-2 text-sm text-zinc-400 hover:text-white transition-colors rounded-lg hover:bg-zinc-800"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 text-sm font-semibold bg-yellow-500 hover:bg-yellow-400 text-black rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? 'Menyimpan…' : isEdit ? 'Simpan Perubahan' : 'Buat Media'}
            </button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">

        {/* ── Informasi Video ──────────────────────────── */}
        <Section title="Informasi Video">
          <div>
            <label className={lbl}>Judul <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              required
              placeholder="Judul video"
              className={inp}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1">
              <label className={lbl}>Group</label>
              {isGroupManager ? (
                <input
                  type="text"
                  value={form.group_slug}
                  disabled
                  className={inp + ' opacity-40 cursor-not-allowed'}
                />
              ) : (
                <select
                  value={form.group_slug}
                  onChange={(e) => set('group_slug', e.target.value)}
                  className={inp}
                >
                  <option value="">— Semua —</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.slug}>{g.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className={lbl}>Kategori</label>
              <select
                value={form.badge}
                onChange={(e) => set('badge', e.target.value)}
                className={inp}
              >
                {BADGE_OPTIONS.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={lbl}>Durasi</label>
              <input
                type="text"
                value={form.duration}
                onChange={(e) => set('duration', e.target.value)}
                placeholder="3:45"
                className={inp}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Tanggal Rilis <span className="text-red-400">*</span></label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => set('date', e.target.value)}
                required
                className={inp + ' [color-scheme:dark]'}
              />
            </div>
            <div>
              <label className={lbl}>YouTube URL</label>
              <input
                type="url"
                value={form.youtube_url}
                onChange={(e) => set('youtube_url', e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className={inp}
              />
            </div>
          </div>
        </Section>

        {/* ── Thumbnail ───────────────────────────────── */}
        <Section title="Thumbnail">
          <div className="grid grid-cols-3 gap-5">
            <div className="col-span-2">
              <label className={lbl}>Gambar Thumbnail</label>
              <AssetImageField
                value={form.image_url}
                onChange={(url) => set('image_url', url)}
                label="Thumbnail"
                feature="media"
              />
            </div>
            <div>
              <label className={lbl}>Alt Text</label>
              <textarea
                value={form.alt}
                onChange={(e) => set('alt', e.target.value)}
                rows={3}
                placeholder="Deskripsi gambar untuk aksesibilitas"
                className={inp + ' resize-none h-[88px]'}
              />
            </div>
          </div>
        </Section>

        {/* ── Pengaturan ──────────────────────────────── */}
        <Section title="Pengaturan">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white font-medium">Featured Video</p>
              <p className="text-xs text-zinc-500 mt-0.5">
                Ditampilkan sebagai video utama di halaman media
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => set('is_featured', !form.is_featured)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                  form.is_featured ? 'bg-yellow-500' : 'bg-zinc-600'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                    form.is_featured ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
              <span className={`text-xs font-medium w-14 ${form.is_featured ? 'text-yellow-400' : 'text-zinc-500'}`}>
                {form.is_featured ? 'Featured' : 'Normal'}
              </span>
            </div>
          </div>
        </Section>

        </div>
      </form>
    </div>
  )
}
